import type {
  GlobalDocument,
  GlobalDocumentBucket,
} from "@/hooks/useGlobalDocuments";

export interface DocsTreeNode extends GlobalDocument {
  children: DocsTreeNode[];
}

/**
 * The fixed "Management" folder at the end of the tree. It is not a database
 * row: its contents are the docs in the managers bucket (admins only).
 */
export const MANAGEMENT_FOLDER_ID = "__management__";

export function managementFolder(children: DocsTreeNode[]): DocsTreeNode {
  return {
    id: MANAGEMENT_FOLDER_ID,
    title: "Management",
    parent_id: null,
    is_folder: true,
    hidden_from_docs: false,
    bucket: "managers",
    document_id: null,
    sort_order: Number.MAX_SAFE_INTEGER,
    created_by: null,
    created_at: "",
    updated_at: "",
    children,
  };
}

export function buildTree(items: GlobalDocument[]): DocsTreeNode[] {
  const map: Record<string, DocsTreeNode> = {};
  const roots: DocsTreeNode[] = [];

  for (const item of items) {
    map[item.id] = { ...item, children: [] };
  }

  for (const item of items) {
    const node = map[item.id];
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort: folders first, then by sort_order, then by title
  const sortNodes = (nodes: DocsTreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.is_folder !== b.is_folder) return a.is_folder ? -1 : 1;
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.title.localeCompare(b.title);
    });
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
}

/** Team docs, followed by the Management folder for admins. */
export function buildDocsTree(
  items: GlobalDocument[],
  showManagement: boolean,
): DocsTreeNode[] {
  const tree = buildTree(items.filter((item) => item.bucket === "team"));
  if (!showManagement) return tree;
  return [
    ...tree,
    managementFolder(
      buildTree(items.filter((item) => item.bucket === "managers")),
    ),
  ];
}

/** Where an item lands when dropped on / moved to `folderId`. */
export function moveTarget(folderId: string | null): {
  parentId: string | null;
  bucket: GlobalDocumentBucket;
} {
  return folderId === MANAGEMENT_FOLDER_ID
    ? { parentId: null, bucket: "managers" }
    : { parentId: folderId, bucket: "team" };
}

/** Check if `targetId` is a descendant of `nodeId` */
export function isDescendant(
  docs: GlobalDocument[],
  nodeId: string,
  targetId: string,
): boolean {
  let current = docs.find((d) => d.id === targetId);
  while (current) {
    if (current.id === nodeId) return true;
    if (!current.parent_id) return false;
    current = docs.find((d) => d.id === current!.parent_id);
  }
  return false;
}

/** Folder ids (incl. the Management folder) that contain `docId`. */
export function ancestorFolderIds(docs: GlobalDocument[], docId: string) {
  const parents: string[] = [];
  let doc = docs.find((d) => d.id === docId);
  while (doc) {
    if (doc.parent_id) {
      parents.push(doc.parent_id);
      doc = docs.find((d) => d.id === doc!.parent_id);
    } else {
      if (doc.bucket === "managers") parents.push(MANAGEMENT_FOLDER_ID);
      break;
    }
  }
  return parents;
}

/** First document (not folder) in tree order below `node`. */
export function firstDocument(nodes: DocsTreeNode[]): DocsTreeNode | null {
  for (const node of nodes) {
    if (!node.is_folder) return node;
    const inner = firstDocument(node.children);
    if (inner) return inner;
  }
  return null;
}
